/**
 * Lightweight rich-text editor.
 *
 * Outputs MODERN inline-styled HTML (no obsolete <font> tags).
 *   • Bold/Italic/Underline use execCommand (styleWithCSS=false) → semantic <b>/<i>/<u>
 *   • Color uses execCommand foreColor with styleWithCSS=true → <span style="color: ...">
 *   • Font size wraps the selection manually in <span style="font-size: Npx"> so the
 *     value isn't restricted to execCommand's 1-7 enum and isn't overridden by Tailwind's
 *     `.prose` styles (inline style attributes always win over class-based CSS).
 */
import { useEffect, useRef, useState } from 'react';
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link as LinkIcon,
  Heading1,
  Heading2,
  Heading3,
  Pilcrow,
  Eraser,
  Undo,
  Redo,
  Type,
} from 'lucide-react';

const SIZE_OPTIONS = [
  { label: 'Liten', value: '13px' },
  { label: 'Normal', value: '16px' },
  { label: 'Stor', value: '22px' },
  { label: 'Mycket stor', value: '32px' },
];

const COLOR_OPTIONS = [
  '#000000', '#dc2626', '#ea580c', '#ca8a04', '#16a34a',
  '#0891b2', '#2563eb', '#7c3aed', '#db2777', '#64748b',
];

/** Wrap the current selection in a <span style="..."> with the given inline style. */
function wrapSelection(styleKey, styleValue) {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
  const range = sel.getRangeAt(0);
  const span = document.createElement('span');
  span.style[styleKey] = styleValue;
  try {
    span.appendChild(range.extractContents());
    range.insertNode(span);
    // Re-select the span contents so subsequent commands keep targeting it
    sel.removeAllRanges();
    const newRange = document.createRange();
    newRange.selectNodeContents(span);
    sel.addRange(newRange);
  } catch (e) {
    // Selection spans complex non-contiguous DOM — fall back to execCommand
    if (styleKey === 'color') document.execCommand('foreColor', false, styleValue);
  }
}

const RichTextEditor = ({ value, onChange, placeholder = 'Skriv innehåll här...', testId, minHeight = 300 }) => {
  const editorRef = useRef(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showSizePicker, setShowSizePicker] = useState(false);

  // Sync external value into the editor only when it's truly different
  // (otherwise the cursor jumps to the end on every keystroke).
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== (value || '')) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    if (onChange && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const focus = () => editorRef.current?.focus();

  const exec = (command, arg = null) => {
    focus();
    document.execCommand(command, false, arg);
    handleInput();
  };

  const applyColor = (color) => {
    focus();
    // styleWithCSS=true makes foreColor output <span style="color: ...">
    // instead of the obsolete <font color="..."> tag that Tailwind .prose ignores.
    document.execCommand('styleWithCSS', false, true);
    document.execCommand('foreColor', false, color);
    document.execCommand('styleWithCSS', false, false);
    handleInput();
  };

  const applySize = (sizeValue) => {
    focus();
    wrapSelection('fontSize', sizeValue);
    handleInput();
  };

  const handleFormatBlock = (tag) => exec('formatBlock', `<${tag}>`);

  const handleLink = () => {
    const url = window.prompt('Klistra in URL:', 'https://');
    if (url && url.trim()) exec('createLink', url.trim());
  };

  const handleClearFormat = () => {
    exec('removeFormat');
    exec('formatBlock', '<p>');
  };

  const Btn = ({ onClick, children, title }) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      className="p-1.5 rounded hover:bg-slate-200 transition-colors text-slate-700"
    >
      {children}
    </button>
  );

  return (
    <div className="border border-slate-300 rounded-lg overflow-hidden bg-white" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-slate-50 border-b border-slate-200">
        <Btn onClick={() => handleFormatBlock('h1')} title="Stor rubrik (H1)"><Heading1 className="w-4 h-4" /></Btn>
        <Btn onClick={() => handleFormatBlock('h2')} title="Mellanrubrik (H2)"><Heading2 className="w-4 h-4" /></Btn>
        <Btn onClick={() => handleFormatBlock('h3')} title="Liten rubrik (H3)"><Heading3 className="w-4 h-4" /></Btn>
        <Btn onClick={() => handleFormatBlock('p')} title="Vanlig text"><Pilcrow className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <Btn onClick={() => exec('bold')} title="Fet (Cmd+B)"><Bold className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec('italic')} title="Kursiv (Cmd+I)"><Italic className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec('underline')} title="Understryken (Cmd+U)"><Underline className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-slate-300 mx-1" />

        {/* Font size */}
        <div className="relative">
          <Btn
            onClick={() => { setShowSizePicker(!showSizePicker); setShowColorPicker(false); }}
            title="Textstorlek"
          >
            <Type className="w-4 h-4" />
          </Btn>
          {showSizePicker && (
            <div className="absolute left-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 py-1 min-w-[140px]">
              {SIZE_OPTIONS.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { applySize(s.value); setShowSizePicker(false); }}
                  className="block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100"
                  style={{ fontSize: s.value }}
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Color picker */}
        <div className="relative">
          <Btn
            onClick={() => { setShowColorPicker(!showColorPicker); setShowSizePicker(false); }}
            title="Textfärg"
          >
            <div className="w-4 h-4 rounded-sm border border-slate-400 flex items-end overflow-hidden">
              <div className="w-full h-1.5 bg-gradient-to-r from-red-500 via-yellow-500 to-blue-500" />
            </div>
          </Btn>
          {showColorPicker && (
            <div className="absolute left-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 p-2 grid grid-cols-5 gap-1">
              {COLOR_OPTIONS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => { applyColor(c); setShowColorPicker(false); }}
                  className="w-6 h-6 rounded border border-slate-200 hover:scale-110 transition-transform"
                  style={{ backgroundColor: c }}
                  title={c}
                />
              ))}
            </div>
          )}
        </div>

        <span className="w-px h-5 bg-slate-300 mx-1" />
        <Btn onClick={() => exec('insertUnorderedList')} title="Punktlista"><List className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec('insertOrderedList')} title="Numrerad lista"><ListOrdered className="w-4 h-4" /></Btn>
        <Btn onClick={handleLink} title="Länk"><LinkIcon className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <Btn onClick={() => exec('undo')} title="Ångra (Cmd+Z)"><Undo className="w-4 h-4" /></Btn>
        <Btn onClick={() => exec('redo')} title="Gör om (Cmd+Shift+Z)"><Redo className="w-4 h-4" /></Btn>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <Btn onClick={handleClearFormat} title="Rensa formatering"><Eraser className="w-4 h-4" /></Btn>
      </div>

      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        data-placeholder={placeholder}
        style={{ minHeight: `${minHeight}px` }}
        className="rich-text-content max-w-none p-4 focus:outline-none text-slate-700 leading-relaxed [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-3 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mb-2 [&_p]:mb-2 [&_a]:text-[#2a9d8f] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&:empty:before]:content-[attr(data-placeholder)] [&:empty:before]:text-slate-400 [&:empty:before]:italic"
      />
    </div>
  );
};

export default RichTextEditor;
